import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import FormatNumberFuncs from '~/Classes/numberFormatFunctions'
import Icons from '~/Common-Objects/UI/Icons/index'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import Paper from '~/Common-Objects/UI/Paper/Paper'
import Select from '~/Common-Objects/UI/Forms/Select/Select'
import SquareChip from '~/Common-Objects/UI/Chips/SquareChip.jsx'
import axios from '~/data/http/axios'
import moment from 'moment'
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'
import Graph from './graph'

export default function LandingPageAnalytics({ landingPage, LandingPageId, setLandingPage, saveLandingPage }) {
    const [forms, setForms] = useState(null)
    const [formId, setFormId] = useState('')
    const [dateRange, setDateRange] = useState('month')
    const [stats, setStats] = useState({
        impressions: { current: 0, previous: 0 },
        submissions: { current: 0, previous: 0 },
        conversionRate: { current: 0, previous: 0 },
        downloads: { current: 0, previous: 0 },
        chats: { current: 0, previous: 0 }
    })

    const getStats = async () => {
        setStats(null)
        let q = '?'
        if (formId) q += 'formId=' + formId + '&'
        if (dateRange) {
            switch (dateRange) {
                case 'day':
                    q += 'startDate=' + moment().subtract(1, 'day').toISOString() + '&'
                    break
                case 'week':
                    q += 'startDate=' + moment().subtract(1, 'week').toISOString() + '&'
                    break
                case 'month':
                    q += 'startDate=' + moment().subtract(1, 'month').toISOString() + '&'
                    break
                case 'year':
                    q += 'startDate=' + moment().subtract(1, 'year').toISOString() + '&'
                    break
            }
            q += 'endDate=' + moment().toISOString() + '&'
        }
        const res = await axios.get('/landingPage/' + LandingPageId + '/analytics/stats' + q)
        setStats(res.data)
    }

    useEffect(() => {
        // go get the data with the formId and dateRange as params
        getStats()
    }, [dateRange, formId])

    useEffect(() => {
        // get forms for landing page
        axios.get('/landingPage/' + LandingPageId + '/forms').then((res) => {
            setForms(res.data)
        })
    }, [])

    if (!forms) return <LoadingIcons />

    const hasChat = !!landingPage.options?.InboxChannelId
    const hasForms = forms.length > 0
    const hasFormsOrChat = hasForms || hasChat

    return (
        <div>
            <div
                className='quickFlex'
                style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}
            >
                {forms.length > 1 && (
                    <Select
                        inlineLabel='Form'
                        value={formId}
                        onChange={(e) => setFormId(e.target.value)}
                        data={[{ label: 'All', value: '' }].concat(
                            forms.map((f) => ({ label: f.formName, value: f.id }))
                        )}
                    />
                )}
                <Select
                    inlineLabel='Date Range'
                    placeholder='Select Date Range'
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    data={[
                        { label: 'Last 24 Hours', value: 'day' },
                        { label: 'Last Week', value: 'week' },
                        { label: 'Last Month', value: 'month' },
                        { label: 'Last Year', value: 'year' }
                    ]}
                />
            </div>
            <div className='row'>
                <div
                    className={
                        hasFormsOrChat ? 'col-xs-12 col-sm-6 col-md col-lg' : 'col-xs-12 col-sm-6 col-md-4 col-lg-3'
                    }
                >
                    <Panel
                        loading={!stats}
                        label='Total Visits'
                        current={stats?.impressions?.current}
                        previous={stats?.impressions?.previous}
                        value={FormatNumberFuncs.formatNumber(stats?.impressions?.current)}
                        icon='eye'
                    />
                </div>
                <div
                    className={
                        hasFormsOrChat ? 'col-xs-12 col-sm-6 col-md col-lg' : 'col-xs-12 col-sm-6 col-md-4 col-lg-3'
                    }
                >
                    <Panel
                        loading={!stats}
                        label='Unique Visitors'
                        current={stats?.visitors?.current}
                        previous={stats?.impvisitorsressions?.previous}
                        value={FormatNumberFuncs.formatNumber(stats?.visitors?.current)}
                        icon='personOutline'
                    />
                </div>
                {hasForms && (
                    <div className='col-xs-12 col-sm-6 col-md col-xs'>
                        <Panel
                            loading={!stats}
                            label='Total Submissions'
                            value={FormatNumberFuncs.formatNumber(stats?.submissions?.current)}
                            increase={
                                ((stats?.submissions?.current - stats?.submissions?.previous) /
                                    stats?.submissions?.previous) *
                                100
                            }
                            icon='personAdd'
                        />
                    </div>
                )}
                {hasForms && (
                    <div className='col-xs-12 col-sm-6 col-md col-xs'>
                        <Panel
                            loading={!stats}
                            label='Conversion Rate'
                            value={FormatNumberFuncs.formatNumber(stats?.conversionRate?.current)}
                            increase={
                                ((stats?.conversionRate?.current - stats?.conversionRate?.previous) /
                                    stats?.conversionRate?.previous) *
                                100
                            }
                            icon='arrowGraphUp'
                        />
                    </div>
                )}
                {forms.some((f) => f.options?.serve) && (
                    <div className='col-xs-12 col-sm-6 col-md col-xs'>
                        <Panel
                            loading={!stats}
                            label='Downloads'
                            current={stats?.downloads?.current}
                            previous={stats?.downloads?.previous}
                            value={FormatNumberFuncs.formatNumber(stats?.downloads?.current)}
                            icon='cloudDownload'
                        />
                    </div>
                )}
                {hasChat && (
                    <div className='col-xs-12 col-sm-6 col-md col-xs'>
                        <Panel
                            loading={!stats}
                            label='Chats'
                            current={stats?.chats?.current}
                            previous={stats?.chats?.previous}
                            value={FormatNumberFuncs.formatNumber(stats?.chats?.current)}
                            icon='chat'
                        />
                    </div>
                )}
            </div>

            <Graph
                LandingPageId={LandingPageId}
                hasForms={hasForms}
                hasChat={hasChat}
                hasGatedContent={forms.some((f) => f.options?.serve)}
            />
        </div>
    )
}

function Panel({ label, value, current, previous, icon, loading }) {
    let _increase
    if (current && previous) _increase = ((current - previous) / previous) * 100
    if (current && !previous) _increase = 100
    if (!current && previous) _increase = -100
    if (!current && !previous) _increase = 0
    if (_increase === Infinity) _increase = 100
    if (_increase === -Infinity) _increase = -100

    return (
        <Paper className='paper-padding-24'>
            <div className='quickFlex' style={{ gap: 16 }}>
                <Icons
                    icon={icon}
                    outerStyle={{
                        borderRadius: 4,
                        backgroundColor: 'var(--indigo-50)',
                        height: 38,
                        width: 38,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    color='var(--indigo-600)'
                    style={{ height: 20, width: 20 }}
                />
                {!loading && (
                    <div>
                        <div className='quickFlex' style={{ gap: 12 }}>
                            <div
                                style={{
                                    fontWeight: 600,
                                    fontStyle: 'Semi Bold',
                                    fontSize: 24,
                                    leadingTrim: 'NONE',
                                    lineHeight: '32px',
                                    letterSpacing: '-0.6px'
                                }}
                            >
                                {value}
                            </div>
                            {_increase !== undefined && (
                                <SquareChip
                                    className={
                                        'small ' +
                                        (_increase > 0 ? 'square-chip-green' : _increase < 0 ? 'square-chip-red' : '')
                                    }
                                    iconLeft={
                                        <Icons
                                            className={_increase < 0 ? 'rotate135' : 'rotateNegative45'}
                                            icon='arrowRight'
                                        />
                                    }
                                >
                                    {_increase.toFixed(1)}%
                                </SquareChip>
                            )}
                        </div>
                        <div
                            style={{
                                fontWeight: 500,
                                fontStyle: 'Medium',
                                fontSize: 14,
                                leadingTrim: 'NONE',
                                lineHeight: '20px',
                                letterSpacing: '0px'
                            }}
                        >
                            {label}
                        </div>
                    </div>
                )}
                {loading && <LoadingIcons />}
            </div>
        </Paper>
    )
}
